// Modules
import { useCallback, useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'

// Hooks
import useNotices from 'hooks/system/useNotices'

// Context
import Dispatch from 'context/Dispatch'

// Reducers
import { AUTHENTICATION_ACTIONS } from 'reducers/authentication'

// Helpers
import { storeToken } from 'helpers/authentication'

// Constants
import { APPLICATION_URL_PATH_PREFIX, LOGIN_URL, THIRD_PARTY_AUTHENTICATION_URL } from 'constants/environment'

// Hook: Authentication > Login
function useLogin () {
	// State
	const [error, setError] = useState(null)
	const [isExpectingCode, setIsExpectingCode] = useState(false)
	const [isProcessing, setIsProcessing] = useState(false)

	// Data
	const navigate = useNavigate()
	const { addNotice } = useNotices()
	const dispatch = useContext(Dispatch)

	// Functions (Memoized)
	const handleResponse = useCallback((response, email) => {
		if (!!response) {
			if (!!response.verified) {
				// TODO: Also handle password expiration and 2FA activation
				storeToken(response) // Store token
				dispatch({ type: AUTHENTICATION_ACTIONS.LOGIN })
				window.dataLayer.push({ event: 'account-login' })
				let redirectionPath = ''
				if (!!APPLICATION_URL_PATH_PREFIX) redirectionPath += APPLICATION_URL_PATH_PREFIX
				redirectionPath += '/login'
				let target = window.location.pathname || '/'
				if (target.startsWith(redirectionPath)) target = '/'
				target += window.location.search
				navigate(target)
			} else navigate(`/verification?email=${encodeURIComponent(email || '')}`)
		}
	}, [dispatch, navigate])
	const loginUsingDefaultMethod = useCallback(async payload => {
		try {
			const { data } = await axios({ data: payload, method: 'POST', url: LOGIN_URL })
			if (!!data) {
				if (!!data.twoFactorAuthRequired) setIsExpectingCode(true)
				else handleResponse(data, payload.email)
			}
		} catch (error) {
			setError(error)
		}
	}, [handleResponse])
	const loginUsingGoogle = useCallback(async payload => {
		try {
			const { data } = await axios({
				data: { credentials: payload.credential },
				method: 'POST',
				url: `${THIRD_PARTY_AUTHENTICATION_URL}/google/login`
			})
			if (!!data?.tokenInfo) handleResponse(data.tokenInfo) // NOTE: "verified" is always true in the response for Google login, so no need for email
			else addNotice({ code: 'client.providerAuthenticationError', type: 'error' })
		} catch (error) {
			addNotice({ code: 'client.providerAuthenticationError', log: error, type: 'error' })
		}
	}, [addNotice, handleResponse])
	const loginUsingApple = useCallback(async payload => {
		try {
			const { data } = await axios({
				data: {
					credentials: payload?.authorization?.id_token,
					firstName: payload?.user?.name?.firstName,
					lastName: payload?.user?.name?.lastName
				},
				method: 'POST',
				url:`${THIRD_PARTY_AUTHENTICATION_URL}/apple/login`
			})
			if (!!data?.tokenInfo) handleResponse(data.tokenInfo) // NOTE: "verified" is always true in the response for Google login, so no need for email
			else addNotice({ code: 'client.providerAuthenticationError', type: 'error' })
		} catch (error) {
			addNotice({ code: 'client.providerAuthenticationError', log: error, type: 'error' })
		}
	}, [addNotice, handleResponse])
	const login = useCallback(async (payload, service) => {
		setIsProcessing(true)
		switch (service) {
			case 'apple':
				await loginUsingApple(payload)
				break
			case 'google':
				await loginUsingGoogle(payload)
				break
			default:
				await loginUsingDefaultMethod(payload)
		}
		setIsProcessing(false)
	}, [loginUsingDefaultMethod, loginUsingApple, loginUsingGoogle])

	// Effects
	useEffect(() => {
		if (!!error) addNotice({ code: `server.${error?.response?.data?.message}`, log: error, type: 'error' })
	}, [addNotice, error])

	// Response
	return { error, isExpectingCode, isProcessing, login }
}

// Export
export default useLogin
